import type { ReactNode } from 'react';
import { Button, TextButton } from '@zillow/constellation-10';
import { SkeletonBlock } from '@/src/components/ui/SkeletonBlock';
import { cx } from '@/styled-system/css';
import type { ApartmentReviews } from '@/src/lib/api/endpoints/apartmentReviews';
import type { ReviewSourceId } from './ReviewsSourceIcon';
import { toReputationPresentation, validReviewCount, validScorePercent } from './reputationPresentation';
import * as styles from './reviewsSkeleton.styles';
import * as reviewsStyles from './reviews.styles';
import * as reputationStyles from './reputation.styles';
import * as redditStyles from './redditReviews.styles';

function TextPlaceholder({ children }: { children: ReactNode }) {
  return (
    <span className={styles.text}>
      <span className={styles.invisible}>{children}</span>
    </span>
  );
}

function WebSkeleton({ reputation }: { reputation?: ApartmentReviews['reputation'] }) {
  const data = reputation ? toReputationPresentation(reputation) : undefined;
  const categories =
    data?.categories ??
    Array.from({ length: data ? 0 : 5 }, () => ({
      category: 'Category name',
      summary: 'A summary of resident feedback about this community and their experience living here.',
      reviewCount: 12,
      scorePercent: 80,
    }));
  return (
    <div className={reputationStyles.stack}>
      <div className={reputationStyles.rating}>
        {(!data || data.averageRating !== undefined) && (
          <span className={reputationStyles.score}>
            <TextPlaceholder>{data?.averageRating?.toFixed(1) ?? '4.3'}</TextPlaceholder>
          </span>
        )}
        <div>
          {(!data || data.averageRating !== undefined) && <SkeletonBlock className={styles.stars} />}
          {(!data || data.totalReviews !== undefined) && (
            <p className={reputationStyles.copy}>
              <TextPlaceholder>
                {data?.totalReviews?.toLocaleString('en-US') ?? '231'} reviews across the web
              </TextPlaceholder>
            </p>
          )}
          <p className={reputationStyles.subtle}>
            <TextPlaceholder>Powered by Reputation</TextPlaceholder>
          </p>
        </div>
      </div>
      {categories.length > 0 && (
        <>
          <div className={reputationStyles.header}>
            <div className={styles.categoryHeading}>
              <TextPlaceholder>Category breakdown</TextPlaceholder>
            </div>
            <span className={reputationStyles.subtle}>
              <TextPlaceholder>{categories.length} categories</TextPlaceholder>
            </span>
          </div>
          <div className={styles.webCards}>
            {categories.slice(0, 5).map((category, index) => {
              const score = validScorePercent(category.scorePercent);
              return (
                <div className={reputationStyles.category} key={index}>
                  <SkeletonBlock className={styles.icon} radius="md" />
                  <div className={reputationStyles.categoryDetail}>
                    <div className={reputationStyles.header}>
                      <h4 className={reputationStyles.title}>
                        <TextPlaceholder>{category.category}</TextPlaceholder>
                      </h4>
                      {score !== undefined && (
                        <span className={reputationStyles.percent}>
                          <TextPlaceholder>{Math.round(score)}%</TextPlaceholder>
                        </span>
                      )}
                    </div>
                    {score !== undefined && <SkeletonBlock className={reputationStyles.meter} />}
                    {validReviewCount(category.reviewCount) !== undefined && (
                      <p className={reputationStyles.subtle}>
                        <TextPlaceholder>
                          Based on {category.reviewCount?.toLocaleString('en-US')}{' '}
                          {category.reviewCount === 1 ? 'review' : 'reviews'}
                        </TextPlaceholder>
                      </p>
                    )}
                    {category.summary && (
                      <>
                        <p className={reputationStyles.summary} data-collapsed="true">
                          <TextPlaceholder>{category.summary}</TextPlaceholder>
                        </p>
                        <div>
                          <TextButton disabled>
                            <TextPlaceholder>Read more</TextPlaceholder>
                          </TextButton>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          {categories.length > 1 && (
            <div className={styles.pagination}>
              {categories.slice(0, 5).map((_, index) => (
                <span className={reputationStyles.pageButton} key={index}>
                  <SkeletonBlock className={reputationStyles.pageDot} />
                </span>
              ))}
            </div>
          )}
          <div className={styles.seeAll} data-count={Math.min(categories.length, 5)}>
            <SkeletonBlock radius="md">
              <span className={styles.invisible}>
                <Button disabled fluid>
                  See all {categories.length} categories
                </Button>
              </span>
            </SkeletonBlock>
          </div>
        </>
      )}
    </div>
  );
}

function RedditSkeleton() {
  return (
    <div className={redditStyles.stack}>
      <p>
        <TextPlaceholder>Explore Reddit discussions about this building and the surrounding area.</TextPlaceholder>
      </p>
      <div className={redditStyles.topics}>
        {['This building', 'Neighborhood', 'Safety', 'Transit & walkability', 'Lifestyle'].map((title) => (
          <div className={redditStyles.topic} key={title}>
            <SkeletonBlock className={styles.icon} radius="md" />
            <div className={redditStyles.topicCopy}>
              <h3 className={redditStyles.topicTitle}>
                <TextPlaceholder>{title}</TextPlaceholder>
              </h3>
              <p className={redditStyles.topicDescription}>
                <TextPlaceholder>What residents say about living in this area.</TextPlaceholder>
              </p>
            </div>
            <SkeletonBlock className={styles.search} />
          </div>
        ))}
      </div>
      <p className={redditStyles.disclaimer}>
        <TextPlaceholder>Content and opinions are from Reddit users, not HotPads.</TextPlaceholder>
      </p>
    </div>
  );
}

export function ReviewsPanelSkeleton({
  source = 'web',
  message = 'Loading reviews',
  reputation,
}: {
  source?: ReviewSourceId;
  message?: string;
  reputation?: ApartmentReviews['reputation'];
}) {
  return (
    <div className={cx(styles.content, source === 'google' && styles.google)} aria-busy="true">
      <output className={styles.visuallyHidden}>{message}</output>
      <div aria-hidden="true">
        {source === 'web' ? (
          <WebSkeleton reputation={reputation} />
        ) : source === 'reddit' ? (
          <RedditSkeleton />
        ) : (
          <div className={styles.googleContent}>
            <SkeletonBlock className={styles.title} />
            <div className={styles.row}>
              <SkeletonBlock className={styles.rating} />
              <SkeletonBlock className={styles.shortLine} />
            </div>
            <SkeletonBlock className={styles.shortLine} />
            <SkeletonBlock className={styles.line} />
            <SkeletonBlock className={styles.line} />
            <SkeletonBlock className={styles.line} />
            <SkeletonBlock className={styles.shortLine} />
            <div className={styles.card}>
              <SkeletonBlock className={styles.shortLine} />
              <SkeletonBlock className={styles.line} />
              <SkeletonBlock className={styles.line} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export function ReviewsLoadingContent() {
  return (
    <>
      <SkeletonBlock className={styles.tabs} radius="md" />
      <ReviewsPanelSkeleton source="google" />
    </>
  );
}

export function ReviewsEntrySkeleton() {
  return (
    <div className={reviewsStyles.entryPointSkeleton} aria-busy="true">
      <output className={styles.visuallyHidden}>Loading reviews</output>
      <SkeletonBlock className={styles.title} />
    </div>
  );
}
